import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect, Link } from "react-router-dom";
import {
    makeStyles,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    TextField,
    TablePagination,
} from "@material-ui/core";
import { DownloadOutlined, FileDoneOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import moment from "moment";
import HttpService from "../../services/httpService";
import { ROLENAME } from "../../utils/index";

// Actions are deep links to each category's page to preserve
// multi-tier approval flows and avoid bypassing checks.
const DEEP_LINK_CATEGORIES = {
    "SKP Approval": () => `/skpApprovals`,
    "Design to Execution": (leadId) => `/validate-contract-documents/${leadId}`,
    "Materials Checklist": (leadId) => `/materials-checklist/${leadId}`,
    "Design Kick-Off": () => `/design_kick-off`,
    "Design Kick-Off - Customer (CHM)": () => `/design_kick-off`,
    "Design Quote Approval": () => `/design-quote-approval-view`,
    "DSO Approval": () => `/dso-payment-approval`,
    "Lost Request": () => `/lost-approvals`,
    "Design Hold Approval": () => `/lead-design-hold-approval`,
};

const ALLOWED_ROLES = [ROLENAME.OperationDesignManager, ROLENAME.AGM_Design];
const DEFAULT_START = "2022-01-01";
const getCurrentDate = () => new Date().toISOString().split("T")[0];

const STATUS_OPTIONS = [
    { value: "Pending for Approval", label: "Pending" },
    { value: "Approved", label: "Approved" },
    { value: "Rejected", label: "Rejected" },
];

const ROLE_OPTIONS = ["ODM", "Designer"];
const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

const STATUS_COLORS = {
    "Approved": { backgroundColor: "#d4edda", color: "#155724" },
    "Rejected": { backgroundColor: "#f8d7da", color: "#721c24" },
    "Pending for Approval": { backgroundColor: "#fff3cd", color: "#856404" },
};

const useStyles = makeStyles(() => ({
    pageWrapper: {
        padding: "2rem",
        borderRadius: "10px",
        boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
        backgroundColor: "#fff",
        marginBottom: "1.5rem",
    },
    headerBanner: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "26px 20px",
        borderRadius: "16px",
        marginBottom: "20px",
        background: "linear-gradient(135deg, #1C375D 0%, #2f5583 100%)",
        boxShadow: "0 6px 18px rgba(28,55,93,0.25)",
    },
    title: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        fontWeight: 800,
        color: "#fff",
        letterSpacing: "0.3px",
        fontSize: "28px",
    },
    titleIcon: {
        fontSize: "26px",
        color: "#F47E46",
    },
    subtitle: {
        display: "inline-block",
        marginTop: "12px",
        padding: "5px 14px",
        borderRadius: "20px",
        backgroundColor: "rgba(255,255,255,0.14)",
        color: "rgba(255,255,255,0.92)",
        fontSize: "12.5px",
        fontWeight: 600,
        maxWidth: "820px",
    },
    headerCell: {
        backgroundColor: "#1C375D",
        color: "#fff",
        fontWeight: "bold",
        fontSize: "13px",
        textAlign: "center",
        border: "1px solid rgba(255,255,255,0.2)",
        whiteSpace: "nowrap",
        padding: "10px 14px",
    },
    bodyCell: {
        textAlign: "center",
        fontSize: "13px",
        padding: "10px 14px",
        border: "1px solid #e2e8f0",
        color: "#263238",
        backgroundColor: "#fff",
    },
    linkStyle: {
        textDecoration: "underline",
        color: "#1C375D",
        fontWeight: 700,
        "&:hover": { color: "#152642" },
    },
    statusBadge: {
        display: "inline-block",
        padding: "4px 10px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: 700,
    },
    csv: {
        display: "flex",
        justifyContent: "flex-end",
        margin: "16px 0",
    },
    noData: {
        textAlign: "center",
        padding: "40px",
        color: "#94a3b8",
        fontSize: "15px",
    },
    submitButton: {
        backgroundColor: "#F47E46 !important",
        color: "white !important",
        "&:hover": { backgroundColor: "#E0663D !important" },
    },
    clearButton: {
        backgroundColor: "#f7b7b7 !important",
        color: "#151921 !important",
    },
    takeActionLink: {
        textDecoration: "underline",
        color: "#1C375D",
        fontWeight: 700,
        fontSize: "12px",
        "&:hover": { color: "#152642" },
    },
}));

const COLS = [
    { key: "customerName", label: "Customer Name" },
    { key: "erpProjectNo", label: "Project No." },
    { key: "lead_no", label: "Lead No." },
    { key: "createdAt", label: "CreatedAt" },
    { key: "salesWonDate", label: "Sales Won Date" },
    { key: "budget", label: "Budget" },
    { key: "category", label: "Category" },
    { key: "requestedBy", label: "Requested By" },
    { key: "assignedTo", label: "Assigned To" },
    { key: "status", label: "Status" },
];

export default function OdmAgmApprovals() {
    const classes = useStyles();
    const { userDetails } = useSelector((state) => state.user);
    const userRoleName = userDetails?.roles?.[0]?.name || "";
    const isAllowed = ALLOWED_ROLES.includes(userRoleName);
    const isAGM = userRoleName === ROLENAME.AGM_Design;

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [filterOptions, setFilterOptions] = useState({ categories: [], users: [] });
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
    const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE_OPTIONS[0]);

    const [filters, setFilters] = useState({
        startDate: DEFAULT_START,
        endDate: getCurrentDate(),
        status: "Pending for Approval",
        category: "",
        user: "",
        role: "ODM",
    });

    useEffect(() => {
        fetchApprovals(filters, 0, rowsPerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchApprovals = async (appliedFilters = filters, appliedPage = page, appliedLimit = rowsPerPage) => {
        setLoading(true);
        try {
            const res = await HttpService.getOdmAgmApprovals({
                ...appliedFilters,
                page: appliedPage + 1, // backend is 1-indexed
                limit: appliedLimit,
            });
            if (res.data?.success) {
                setRows(res.data.rows || []);
                setFilterOptions({ categories: res.data.categories || [], users: res.data.users || [] });
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            console.error("Error fetching approvals:", err);
        }
        setLoading(false);
    };

    const handleFilterChange = (name, value) =>
        setFilters((prev) => {
            const next = { ...prev, [name]: value };
            if (name === "role") {
                next.user = ""; // User list depends on Role
                // Refetch immediately so the User dropdown reflects the new Role right away,
                // instead of showing the previous Role's (stale) list until Submit is clicked.
                setPage(0);
                fetchApprovals(next, 0, rowsPerPage);
            }
            return next;
        });

    const applyFilters = () => {
        setPage(0);
        fetchApprovals(filters, 0, rowsPerPage);
    };

    const clearFilters = () => {
        const def = { startDate: DEFAULT_START, endDate: getCurrentDate(), status: "Pending for Approval", category: "", user: "", role: "ODM" };
        setFilters(def);
        setPage(0);
        fetchApprovals(def, 0, rowsPerPage);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        fetchApprovals(filters, newPage, rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newLimit = parseInt(event.target.value, 10);
        setRowsPerPage(newLimit);
        setPage(0);
        fetchApprovals(filters, 0, newLimit);
    };

    const csvHeaders = [
        { label: "Sl. No.", key: "slno" },
        ...COLS.map((c) => ({ label: c.label, key: c.key })),
    ];

    const csvData = rows.map((row, idx) => ({
        slno: page * rowsPerPage + idx + 1,
        ...row,
        createdAt: row.createdAt ? moment(row.createdAt).format("DD-MM-YYYY") : "-",
        salesWonDate: row.salesWonDate ? moment(row.salesWonDate).format("DD-MM-YYYY") : "-",
    }));

    if (userDetails?._id && !isAllowed) return <Redirect to="/unauthorized" />;

    return (
        <Paper className={classes.pageWrapper}>
            <div className={classes.headerBanner}>
                <Typography className={classes.title}>
                    <FileDoneOutlined className={classes.titleIcon} />
                    My Approval Dashboard
                </Typography>
                <Typography className={classes.subtitle}>
                    {isAGM
                        ? "Approval requests pending with you — Design Quote Approval and Design to Execution"
                        : "Approval requests pending with you — SKP, Materials Checklist, Design to Execution, Design Kick-Off (Customer/CHM), Design Quote Approval and Lost Request"}
                </Typography>
            </div>

            <Paper elevation={2} style={{ padding: "16px", marginBottom: "24px" }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Sales Won Start Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={filters.startDate}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: getCurrentDate() }}
                            onChange={(e) => handleFilterChange("startDate", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Sales Won End Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={filters.endDate}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) => handleFilterChange("endDate", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl variant="outlined" fullWidth size="small">
                            <InputLabel id="category-label">Category</InputLabel>
                            <Select
                                labelId="category-label"
                                label="Category"
                                value={filters.category}
                                onChange={(e) => handleFilterChange("category", e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {filterOptions.categories.map((c) => (
                                    <MenuItem key={c} value={c}>{c}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {isAGM && (
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel id="role-label">Role</InputLabel>
                                <Select
                                    labelId="role-label"
                                    label="Role"
                                    value={filters.role}
                                    onChange={(e) => handleFilterChange("role", e.target.value)}
                                >
                                    {ROLE_OPTIONS.map((r) => (
                                        <MenuItem key={r} value={r}>{r}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl variant="outlined" fullWidth size="small">
                            <InputLabel id="user-label">User</InputLabel>
                            <Select
                                labelId="user-label"
                                label="User"
                                value={filters.user}
                                onChange={(e) => handleFilterChange("user", e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {filterOptions.users.map((u) => (
                                    <MenuItem key={u._id} value={u._id}>{u.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl variant="outlined" fullWidth size="small">
                            <InputLabel id="status-label">Status</InputLabel>
                            <Select
                                labelId="status-label"
                                label="Status"
                                value={filters.status}
                                onChange={(e) => handleFilterChange("status", e.target.value)}
                            >
                                {STATUS_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button variant="contained" className={classes.submitButton} onClick={applyFilters}>Submit</Button>
                            <Button variant="contained" className={classes.clearButton} onClick={clearFilters}>Clear</Button>
                        </div>
                    </Grid>
                </Grid>
            </Paper>

            {rows.length > 0 && (
                <div className={classes.csv}>
                    <CSVLink data={csvData} headers={csvHeaders} filename={`Approvals_${new Date().toISOString().split("T")[0]}.csv`} style={{ textDecoration: "none" }}>
                        <button style={{ display: "flex", alignItems: "center", gap: "6px", backgroundColor: "#355025", color: "#FFF2EB", border: "none", borderRadius: "4px", padding: "7px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                            <DownloadOutlined /> Download CSV
                        </button>
                    </CSVLink>
                </div>
            )}

            {loading ? (
                <Typography className={classes.noData}>Loading...</Typography>
            ) : rows.length === 0 ? (
                <Typography className={classes.noData}>No approvals found for the selected filters</Typography>
            ) : (
                <TableContainer style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.headerCell}>Sl. No.</TableCell>
                                {COLS.map((c) => (
                                    <TableCell key={c.key} className={classes.headerCell}>{c.label}</TableCell>
                                ))}
                                <TableCell className={classes.headerCell}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, idx) => (
                                <TableRow key={`${row.leadId}-${row.category}`}>
                                    <TableCell className={classes.bodyCell}>{page * rowsPerPage + idx + 1}</TableCell>
                                    {COLS.map((c) => {
                                        if (c.key === "lead_no" || c.key === "erpProjectNo") {
                                            return (
                                                <TableCell key={c.key} className={classes.bodyCell}>
                                                    <Link
                                                        to={`/project-logs/${row.leadId}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={classes.linkStyle}
                                                    >
                                                        {row[c.key]}
                                                    </Link>
                                                </TableCell>
                                            );
                                        }
                                        if (c.key === "createdAt") {
                                            return (
                                                <TableCell key={c.key} className={classes.bodyCell}>
                                                    {row.createdAt ? moment(row.createdAt).format("DD-MM-YYYY") : "-"}
                                                </TableCell>
                                            );
                                        }
                                        if (c.key === "salesWonDate") {
                                            return (
                                                <TableCell key={c.key} className={classes.bodyCell}>
                                                    {row.salesWonDate ? moment(row.salesWonDate).format("DD-MM-YYYY") : "-"}
                                                </TableCell>
                                            );
                                        }
                                        if (c.key === "status") {
                                            return (
                                                <TableCell key={c.key} className={classes.bodyCell}>
                                                    <span className={classes.statusBadge} style={STATUS_COLORS[row.status] || {}}>
                                                        {row.status}
                                                    </span>
                                                </TableCell>
                                            );
                                        }
                                        return (
                                            <TableCell key={c.key} className={classes.bodyCell}>{row[c.key]}</TableCell>
                                        );
                                    })}
                                    <TableCell className={classes.bodyCell}>
                                        {row.status !== "Pending for Approval" ? (
                                            "-"
                                        ) : DEEP_LINK_CATEGORIES[row.category] ? (
                                            <a
                                                href={DEEP_LINK_CATEGORIES[row.category](row.leadId)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={classes.takeActionLink}
                                            >
                                                Take Action →
                                            </a>
                                        ) : "-"}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {!loading && rows.length > 0 && (
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                />
            )}
        </Paper>
    );
}
